import React, { createContext, useContext, useEffect, useRef } from 'react';
import {Driver, DriveStep, driver} from 'driver.js'
import "driver.js/dist/driver.css";
import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from '../../../setup'
import {IWorkspaceState} from '../../@reducer/WorkspaceRedux'

interface DriverContextType {
    startDriver: (steps: DriveStep[]) => void;
    resetDriver: () => void;
}

const DriverContext = createContext<DriverContextType | null>(null);

export const useDriver = () => {
    const context = useContext(DriverContext);
    if (!context) {
        throw new Error('useDriver must be used within a DriverProvider');
    }
    return context;
};

const localization = {
    fr: {
        doneBtnText: "Terminer",
        closeBtnText: "Fermer",
        nextBtnText: "Suivant",
        prevBtnText: "Précédent",
        progressText: "{{current}} sur {{total}}",
    },
    en: {
        doneBtnText: "Finish",
        closeBtnText: "Close",
        nextBtnText: "Next",
        prevBtnText: "Previous",
        progressText: "Step {{current}} of {{total}}",
    }
};

export const DriverProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    const driverRef = useRef<Driver | null>(null);
    const {
        colors: {
            secondary,
        },
    } = useSelector<RootState>(({workspace}) => workspace, shallowEqual) as IWorkspaceState

    useEffect(() => {
        driverRef.current = driver({
            popoverClass: 'driverjs-theme',
            animate: true,
            overlayOpacity: .35,
            showProgress: true,
            overlayColor: secondary,
            showButtons: [
                'next',
                'previous',
                // 'close'
            ],
            ...localization.fr,
            onDestroyStarted: () => {
                // eslint-disable-next-line no-restricted-globals
                if (!driverRef.current?.hasNextStep() || confirm("Êtes-vous sûr de vouloir quitter le tutoriel ?")) {
                    driverRef.current?.destroy();
                }
            }
        });

        return () => {
            if (driverRef.current) {
                driverRef.current.destroy();
            }
        };
    }, []);

    const startDriver = (steps: DriveStep[]) => {
        if (driverRef.current) {
            driverRef.current.setSteps(steps);
            driverRef.current.drive();
        }
    };

    const resetDriver = () => {
        if (driverRef.current) {
            driverRef.current.destroy();
        }
    };

    return (
        <DriverContext.Provider value={{ startDriver, resetDriver }}>
            {children}
        </DriverContext.Provider>
    );
};